import AppError from '@/components/AppError';
import { TappIcon } from '@/components/ui/TappIcon';
import { TappInputFilter } from '@/components/ui/TappInputFilter';
import { TappLink } from '@/components/ui/TappLink';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { TappPaginator } from '@/components/ui/TappPaginator';
import { TappPillProps, TappPill } from '@/components/ui/TappPill';
import { TappSelectFilter } from '@/components/ui/TappSelectFilter';
import { useApiTable, TappTableWrapper, TappTable } from '@/components/ui/TappTable';
import { cn } from '@/components/ui/utils';
import { SupportRequest, SupportRequestStatus } from '@/lib/models/support';
import { ApiUtils } from '@/lib/utils/api';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAlert } from '@/components/ui/alert-dialog-provider';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { TappSelect } from '@/components/ui/TappSelect';
import { TappLabel } from '@/components/ui/TappLabel';
import { TappFormField } from '@/components/ui/TappFormField';

dayjs.extend(relativeTime);

const statusOptions = [
  { label: 'Nou', value: SupportRequestStatus.new },
  { label: 'Rezolvat', value: SupportRequestStatus.resolved },
];

const SupportPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <TappPageHeader title={t('support')} backLink="/admin" />
      <Table />
    </>
  );
};

const TableFilters = () => {
  const { t } = useTranslation();

  return (
    <section className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1">
      <TappInputFilter
        name="search"
        border
        placeholder={t('searchSupport')}
        prefix={<TappIcon>search</TappIcon>}
        className="min-w-[240px]"
        clearable
      />

      <TappSelectFilter
        name="status"
        placeholder={t('supportStatus')}
        options={[
          { label: t('new'), value: SupportRequestStatus.new },
          { label: t('resolved'), value: SupportRequestStatus.resolved },
        ]}
        border
        className="min-w-[180px]"
      />

      <TappSelectFilter
        name="userType"
        placeholder={t('userType')}
        options={[
          { label: t('driver'), value: 'Driver' },
          { label: t('customer'), value: 'Customer' },
        ]}
        border
        className="min-w-[180px]"
      />
    </section>
  );
};

const Table = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const alert = useAlert();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<SupportRequestStatus | null>(null);

  const { mutateAsync: updateStatus, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, status }: { id: string; status: SupportRequestStatus }) =>
      ApiUtils.put(`support/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support'] });
    },
  });

  const handleEditClick = (request: SupportRequest) => {
    setSelectedRequest(request);
    setSelectedStatus(request.status);
    setEditModalOpen(true);
  };

  const handleSaveStatus = async () => {
    if (!selectedRequest || !selectedStatus) return;

    try {
      await updateStatus({ id: selectedRequest._id, status: selectedStatus });
      await alert({
        title: t('success'),
        body: t('supportStatusUpdated'),
      });
      setEditModalOpen(false);
      setSelectedRequest(null);
      setSelectedStatus(null);
    } catch (err: any) {
      console.error(err);
      await alert({
        title: t('error'),
        body: ApiUtils.convertApiErrorToString(err),
      });
    }
  };

  const statusColors: Record<SupportRequestStatus, TappPillProps['color']> = {
    [SupportRequestStatus.new]: 'destructive',
    [SupportRequestStatus.resolved]: 'success',
  };

  const userTypeColors: Record<'driver' | 'customer', TappPillProps['color']> = {
    driver: 'amber',
    customer: 'primary',
  };

  const columnHelper = createColumnHelper<SupportRequest>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('userName', {
        header: t('user'),
        enableSorting: true,
      }),
      columnHelper.accessor('userType', {
        header: t('userType'),
        enableSorting: true,
        cell: (info) => {
          const userType = info.getValue().toLowerCase() as 'driver' | 'customer';
          return <TappPill color={userTypeColors[userType]}>{t(userType)}</TappPill>;
        },
      }),
      columnHelper.accessor('rideId', {
        header: t('ride'),
        enableSorting: false,
        cell: (info) => (
          <TappLink to={`/admin/rides/${info.getValue()}`} className="text-primary hover:underline">
            {info.getValue().slice(-8)}
          </TappLink>
        ),
      }),
      columnHelper.accessor('message', {
        header: t('message'),
        enableSorting: false,
        cell: (info) => (
          <div className="max-w-xs overflow-hidden text-ellipsis" title={info.getValue()}>
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: t('date'),
        enableSorting: true,
        cell: (info) => <span>{dayjs(info.getValue()).format('DD.MM.YYYY HH:mm')}</span>,
      }),
      columnHelper.accessor('status', {
        header: t('status'),
        enableSorting: true,
        cell: (info) => <TappPill color={statusColors[info.getValue()]}>{t(info.getValue())}</TappPill>,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <Button variant="default" size="sm" onClick={() => handleEditClick(original)}>
            {t('edit')}
          </Button>
        ),
      }),
    ],
    [t]
  );

  const { table, isLoading, total, error } = useApiTable<SupportRequest>({
    columns,
    queryKey: (filter) => ['support', filter],
    queryFn: async (filter) => {
      const response = await ApiUtils.get<{
        data: {
          supportRequests: SupportRequest[];
          pagination: { total: number; pageIndex: number; pageSize: number; totalPages: number };
        };
      }>('support', {
        query: { ...filter },
      });
      return {
        data: response.data.supportRequests,
        total: response.data.pagination.total,
      };
    },
    enableSortingRemoval: true,
  });

  if (error) return <AppError error={error} />;

  return (
    <>
      <TableFilters />
      <TappTableWrapper className={cn({ isLoading })}>
        <TappTable table={table} stickyHeader />
        <TappPaginator table={table} total={total} />
      </TappTableWrapper>

      <AlertDialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <AlertDialogContent aria-describedby={undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('editSupportStatus')}</AlertDialogTitle>
          </AlertDialogHeader>

          {selectedRequest && (
            <div className="space-y-2 text-left text-sm text-muted-foreground">
              <p>
                <strong>{t('user')}:</strong> {selectedRequest.userName} ({t(selectedRequest.userType)})
              </p>
              <p>
                <strong>{t('message')}:</strong> {selectedRequest.message}
              </p>
              <p>
                <strong>{t('date')}:</strong> {dayjs(selectedRequest.createdAt).format('DD.MM.YYYY HH:mm')}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <TappLabel>{t('status')}</TappLabel>
            <TappSelect
              className="w-full"
              options={statusOptions}
              value={statusOptions.find((o) => o.value === selectedStatus)}
              onChange={(val) => setSelectedStatus(val?.value as SupportRequestStatus)}
              menuPortalTarget={null}
            />
          </div>

          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditModalOpen(false);
                setSelectedRequest(null);
                setSelectedStatus(null);
              }}
            >
              {t('cancel')}
            </Button>
            <Button onClick={handleSaveStatus} disabled={isUpdating}>
              {t('save')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SupportPage;
